import { useState, useRef, useEffect } from 'react';
import { Box, Paper, Stack, TextField, Button, Typography, CircularProgress, InputAdornment,
         IconButton, useMediaQuery } from '@mui/material';
import { Visibility, VisibilityOff, ArrowForward, PersonOutline, LockOutlined,
         WarningAmberRounded, AccessTimeRounded, InfoOutlined } from '@mui/icons-material';
import { auth } from '../Common';
import Skyline from './Skyline';
import sfondo from '../assets/sfondo-accesso.jpg';

/**
 * Accesso all'app, dai mockup del 24/09/2026. Nella stessa schermata: accesso, errore
 * (credenziali errate o blocco dopo troppi tentativi), cambio password obbligatorio al
 * primo ingresso o dopo una reimpostazione, e "Non ricordi la password?" (richiesta del
 * link via email, poi scelta della nuova password arrivando da quel link).
 *
 * Telefono prima di tutto: colonna singola, etichette SOPRA i campi (si leggono
 * meglio anche in piena luce), campi e pulsante alti. Dietro, l'immagine del mockup
 * (cartellone con il logo, città, viale): velata su telefono, piena su desktop, dove
 * la scheda galleggia a destra.
 */

const NAVY = '#0f172a';
const ARANCIO = '#FD5900';

function Avviso({ tipo, titolo, children }) {
  const s = {
    errore: { bg: '#FFF4ED', icona: <WarningAmberRounded sx={{ color: '#F97316' }} />, colore: '#DC2626', titolo: '#9a3412' },
    blocco: { bg: '#FFF4ED', icona: <AccessTimeRounded sx={{ color: '#F97316' }} />, colore: NAVY, titolo: '#9a3412' },
    info:   { bg: '#EFF6FF', icona: <InfoOutlined sx={{ color: '#1d4ed8' }} />, colore: NAVY, titolo: '#1e40af' },
  }[tipo];
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" role="alert"
           sx={{ bgcolor: s.bg, borderRadius: 2.5, px: 2, py: 1.4 }}>
      {s.icona}
      <Box>
        {titolo && (
          <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.8, color: s.titolo, textTransform: 'uppercase' }}>
            {titolo}
          </Typography>
        )}
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: s.colore, lineHeight: 1.35 }}>{children}</Typography>
      </Box>
    </Stack>
  );
}

/** Campo con etichetta sopra e icona dentro, come nei mockup. */
function Campo({ id, etichetta, icona, errore, aiuto, fine, ...resto }) {
  return (
    <Box>
      <Typography component="label" htmlFor={id}
                  sx={{ display: 'block', fontWeight: 700, fontSize: 14.5, color: NAVY, mb: 0.75 }}>
        {etichetta}
      </Typography>
      <TextField id={id} fullWidth error={errore} helperText={aiuto}
                 InputProps={{
                   startAdornment: <InputAdornment position="start">{icona}</InputAdornment>,
                   endAdornment: fine,
                 }}
                 sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: '#fff', minHeight: 52 },
                       '& .MuiFormHelperText-root': { mx: 0.5 } }}
                 {...resto} />
    </Box>
  );
}

/** Piè di pagina: profilo di Palermo, solo decorazione. */
function Profilo({ larghezza }) {
  return <Skyline style={{ width: larghezza, height: 'auto', display: 'block', margin: '0 auto' }} />;
}

// Margine destro della scheda su desktop.
const MARGINE = 'clamp(32px, 5vw, 120px)';
// Velo sull'immagine su telefono: il colore di fondo dell'app, al 70%.
const VELO = '#F4F6FA';
const VELO_TRASPARENTE = 'rgba(244,246,250,.7)';
// Altezza dell'immagine su desktop: quanto basta a coprire (come cover), ma mai oltre il
// 125% dello schermo. Sugli schermi ultra larghi (32:9) cover ne tagliava il 60% in
// altezza; così si perde al massimo il 20% e a destra la scena prosegue sfocata
// (l'immagine è allungata apposta, vedi brand/genera_asset.py).
const ALTEZZA_SFONDO = 'clamp(100vh, 62.5vw, 125vh)';
// Il titolo parte subito dopo il grattacielo più alto (bordo destro a x = 515 su un'altezza
// di 992): da lì in su dietro al testo c'è solo cielo. Più a sinistra il sottotitolo
// finiva sul vetro del grattacielo, con contrasto 2,7:1.
const DOPO_GRATTACIELO = `calc(${ALTEZZA_SFONDO} * 0.5192 + 1vw)`;

/**
 * Titolo nel cielo fra il cartellone e la scheda, come nel mockup. Si mostra solo dove
 * ci sta su tre righe senza toccare nessuno dei due (vedi `conTitolo`).
 */
function Titolo() {
  return (
    <Box sx={{ position: 'absolute', top: '37%', transform: 'translateY(-50%)',
               left: DOPO_GRATTACIELO, right: `calc(${MARGINE} + 440px + 3vw)`, color: '#0B1F4D' }}>
      <Typography component="h1" sx={{ fontSize: 'clamp(30px, 2.8vw, 54px)', fontWeight: 800, lineHeight: 1.15, letterSpacing: -0.5 }}>
        Censimento<br />dei cartelloni
      </Typography>
      <Box sx={{ width: 48, height: 4, bgcolor: ARANCIO, borderRadius: 2, my: 2.2 }} />
      <Typography sx={{ fontSize: 'clamp(16px, 1.3vw, 22px)', color: '#1e293b', fontWeight: 500, lineHeight: 1.45 }}>
        Foto e posizione di ogni impianto,<br />dal rilievo sul campo alla mappa.
      </Typography>
    </Box>
  );
}

export default function Login({ utenteDaAggiornare, messaggio, onEntrato, onFineReset }) {
  const desktop = useMediaQuery('(min-width:900px)');
  // Sotto i 1280 px, o su schermi quasi quadrati, fra grattacielo e scheda non c'è posto
  // (sotto i 640 px di altezza la pagina si allunga oltre lo schermo e i conti in vh non tornano)
  const conTitolo = useMediaQuery('(min-width:1280px) and (min-height:640px) and (min-aspect-ratio: 3/2)');
  const cambio = !!utenteDaAggiornare;

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [nuova, setNuova] = useState('');
  const [conferma, setConferma] = useState('');
  const [mostra, setMostra] = useState(false);
  // { tipo: 'credenziali' | 'blocco' | 'contratto' | 'nuova' | 'conferma' | 'chi' | 'rete' | 'generico', testo }
  const [errore, setErrore] = useState(null);
  const [busy, setBusy] = useState(false);
  const pwRef = useRef(null);

  // "Non ricordi la password?". Viste: accesso, dimenticata (chiede il link), reimposta
  // (arrivati dal link dell'email, ?reimposta=…: il token si legge una volta sola).
  const [tokenReset] = useState(() => new URLSearchParams(window.location.search).get('reimposta'));
  const [vista, setVista] = useState(tokenReset ? 'reimposta' : 'accesso');
  const [chi, setChi] = useState('');                        // nome utente o email per il link
  const [inviato, setInviato] = useState(false);
  const [resetUtente, setResetUtente] = useState(null);      // { username, nome, tipo } se il link vale
  const [resetScaduto, setResetScaduto] = useState(null);    // perché il link non vale
  const [avvisoOk, setAvvisoOk] = useState(null);
  useEffect(() => {
    if (!tokenReset) return;
    auth.verificaReset(tokenReset).then(setResetUtente)
      .catch((e) => setResetScaduto(e.status ? e.message : 'Connessione non riuscita: controlla la rete e riprova.'));
  }, [tokenReset]);
  const togliLink = (fatto) => {
    if (!tokenReset) return;
    window.history.replaceState(null, '', window.location.pathname + window.location.hash);
    onFineReset?.(fatto);
  };
  const vaiA = (v) => {
    setErrore(null); setInviato(false); setAvvisoOk(null);
    if (v === 'dimenticata' && !chi && username.trim()) setChi(username.trim());
    if (vista === 'reimposta') togliLink(false);
    setVista(v);
  };
  const sceltaPassword = cambio || (vista === 'reimposta' && !!resetUtente);

  const occhio = (
    <InputAdornment position="end">
      <IconButton onClick={() => setMostra(m => !m)} edge="end" aria-label={mostra ? 'nascondi password' : 'mostra password'}>
        {mostra ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );
  const icona = (C) => <C sx={{ color: '#64748b' }} />;
  const pulisci = () => { if (errore) setErrore(null); };

  const invia = async (e) => {
    e.preventDefault();
    setErrore(null);
    const errRete = (err) => ({ tipo: err.status ? 'generico' : 'rete',
                                testo: err.status ? err.message : 'Connessione non riuscita: controlla la rete e riprova.' });
    if (vista === 'dimenticata') {
      if (inviato) return vaiA('accesso');
      if (!chi.trim()) return setErrore({ tipo: 'chi', testo: 'Scrivi il tuo nome utente o la tua email.' });
      setBusy(true);
      try { await auth.passwordDimenticata(chi.trim()); setInviato(true); }
      catch (err) { setErrore(err.status === 429 ? { tipo: 'blocco', testo: err.message } : errRete(err)); }
      finally { setBusy(false); }
      return;
    }
    if (vista === 'reimposta' && !cambio) {
      if (!resetUtente) return vaiA('dimenticata');   // link non valido: se ne chiede uno nuovo
      if (nuova.length < 8) return setErrore({ tipo: 'nuova', testo: 'La nuova password deve avere almeno 8 caratteri.' });
      if (nuova !== conferma) return setErrore({ tipo: 'conferma', testo: 'Le due password non coincidono.' });
      setBusy(true);
      try {
        const r = await auth.reimposta(tokenReset, nuova);
        setNuova(''); setConferma(''); setUsername(r.username); setPassword('');
        setVista('accesso');
        setAvvisoOk('Password aggiornata: ora accedi con la nuova.');
        togliLink(true);
      } catch (err) {
        if (err.codice === 'reset') { setResetUtente(null); setResetScaduto(err.message); }
        else setErrore(errRete(err));
      } finally { setBusy(false); }
      return;
    }
    if (cambio) {
      if (nuova.length < 8) return setErrore({ tipo: 'nuova', testo: 'La nuova password deve avere almeno 8 caratteri.' });
      if (nuova !== conferma) return setErrore({ tipo: 'conferma', testo: 'Le due password non coincidono.' });
      setBusy(true);
      try {
        await auth.cambiaPassword(nuova, null);
        onEntrato(await auth.me());
      } catch (err) {
        setErrore({ tipo: err.status ? 'generico' : 'rete',
                    testo: err.status ? err.message : 'Connessione non riuscita: controlla la rete e riprova.' });
      } finally { setBusy(false); }
      return;
    }
    if (!username.trim() || !password) return setErrore({ tipo: 'credenziali', testo: 'Inserisci nome utente e password.' });
    setBusy(true);
    try {
      const u = await auth.login(username.trim(), password);
      setPassword('');
      onEntrato(u);
    } catch (err) {
      // 429 = blocco dopo troppi tentativi; senza codice = rete assente (frequente sul campo)
      if (err.status === 429) setErrore({ tipo: 'blocco', testo: err.message });
      else if (!err.status) setErrore({ tipo: 'rete', testo: 'Connessione non riuscita: controlla la rete e riprova.' });
      // azienda sospesa o contratto non attivo: credenziali giuste, accesso chiuso, e si dice perché
      else if (err.codice === 'contratto') setErrore({ tipo: 'contratto', testo: err.message });
      else setErrore({ tipo: err.status === 401 ? 'credenziali' : 'generico', testo: err.message });
      // Si svuota la password (non il nome utente): chi ha sbagliato riprova subito.
      setPassword('');
      setTimeout(() => pwRef.current?.focus(), 0);
    } finally { setBusy(false); }
  };

  // Bordo rosso su ENTRAMBI i campi: di proposito non si dice quale dei due è sbagliato.
  const credenzialiErrate = errore?.tipo === 'credenziali';
  // saluto col nome proprio ("Ciao Mario,"); l'account di una concessionaria porta il nome
  // dell'azienda, che va detto intero ("Ciao COMUNE DI PALERMO,", non "Ciao COMUNE,")
  const salutoDi = (nomeIntero, tipo) => tipo === 'concessionario' ? (nomeIntero || '') : (nomeIntero || '').split(/\s+/)[0];
  const nome = cambio ? salutoDi(utenteDaAggiornare?.nome, utenteDaAggiornare?.azienda?.tipo)
                      : salutoDi(resetUtente?.nome, resetUtente?.tipo);
  const verificaLink = vista === 'reimposta' && !cambio && !resetUtente && !resetScaduto;

  const scheda = (
    <Paper component="form" onSubmit={invia} noValidate elevation={0}
           sx={{ width: '100%', maxWidth: 440, p: { xs: 3, sm: 4.5 }, borderRadius: 4,
                 boxShadow: desktop ? '0 24px 64px rgba(15,23,42,.24)' : '0 16px 48px rgba(15,23,42,.16)' }}>
      <Stack spacing={2.4}>
        <Stack alignItems="center" spacing={1.2}>
          <img src="/logo192.png" alt="" width={104} height={104} style={{ display: 'block' }} />
          <img src="/scritta.png" alt="AffariAffissioni"
               style={{ display: 'block', width: '100%', maxWidth: 250, height: 'auto' }} />
        </Stack>

        {sceltaPassword ? (
          <Box sx={{ textAlign: desktop ? 'center' : 'left' }}>
            <Typography sx={{ fontSize: 26, fontWeight: 800, color: NAVY, lineHeight: 1.2 }}>Ciao {nome},</Typography>
            <Typography sx={{ fontSize: 18, color: '#334155', mt: 0.4, lineHeight: 1.35 }}>
              {cambio ? 'scegli una nuova password per continuare' : 'scegli la tua nuova password'}
            </Typography>
          </Box>
        ) : vista !== 'accesso' ? (
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: 22, fontWeight: 800, color: NAVY, lineHeight: 1.25 }}>
              {vista === 'dimenticata' ? 'Non ricordi la password?' : 'Reimposta la password'}
            </Typography>
            {vista === 'dimenticata' && !inviato && (
              <Typography sx={{ color: '#334155', fontSize: 15.5, mt: 0.75 }}>
                Scrivi il tuo nome utente o la tua email: ti mandiamo un link per sceglierne una nuova.
              </Typography>
            )}
          </Box>
        ) : (
          <Typography sx={{ color: '#334155', fontSize: 15.5, textAlign: 'center' }}>
            Accedi per consultare e censire i cartelloni
          </Typography>
        )}

        {vista === 'accesso' && (avvisoOk || messaggio) && !errore && <Avviso tipo="info">{avvisoOk || messaggio}</Avviso>}
        {vista === 'dimenticata' && inviato && (
          <Avviso tipo="info" titolo="Controlla la posta">
            Se il nome utente o l'email corrispondono a un account con un indirizzo email, ti abbiamo mandato
            un link valido un'ora. Non arriva? Guarda nello spam, oppure chiedi a un amministratore di
            AffariAffissioni di reimpostarla.
          </Avviso>
        )}
        {vista === 'reimposta' && resetScaduto && <Avviso tipo="blocco" titolo="Link non valido">{resetScaduto}</Avviso>}
        {errore?.tipo === 'blocco' && (
          <Avviso tipo="blocco" titolo={vista === 'dimenticata' ? 'Troppe richieste' : 'Accesso temporaneamente bloccato'}>
            {errore.testo}
          </Avviso>
        )}
        {errore?.tipo === 'contratto' && <Avviso tipo="blocco" titolo="Accesso non attivo">{errore.testo}</Avviso>}
        {errore && errore.tipo !== 'blocco' && errore.tipo !== 'contratto' && <Avviso tipo="errore">{errore.testo}</Avviso>}

        {vista === 'dimenticata' && !cambio ? (
          !inviato && (
            <Campo id="dimenticata-chi" etichetta="Nome utente o email" icona={icona(PersonOutline)}
                   placeholder="Il tuo nome utente o la tua email" value={chi} autoFocus={desktop}
                   errore={errore?.tipo === 'chi'} autoComplete="username"
                   inputProps={{ autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false }}
                   onChange={(e) => { setChi(e.target.value); pulisci(); }} />
          )
        ) : vista === 'reimposta' && !sceltaPassword ? null : !sceltaPassword ? (
          <>
            {/* niente autoFocus su telefono: aprirebbe subito la tastiera e coprirebbe la pagina */}
            <Campo id="accesso-utente" etichetta="Nome utente" icona={icona(PersonOutline)}
                   placeholder="Inserisci il tuo nome utente" value={username} autoFocus={desktop}
                   errore={credenzialiErrate} autoComplete="username"
                   inputProps={{ autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false }}
                   onChange={(e) => { setUsername(e.target.value); pulisci(); }} />
            <Campo id="accesso-password" etichetta="Password" icona={icona(LockOutlined)}
                   placeholder="Inserisci la password" value={password} inputRef={pwRef}
                   type={mostra ? 'text' : 'password'} autoComplete="current-password"
                   errore={credenzialiErrate} fine={occhio}
                   onChange={(e) => { setPassword(e.target.value); pulisci(); }} />
          </>
        ) : (
          <>
            <Campo id="nuova-password" etichetta="Nuova password" icona={icona(LockOutlined)}
                   placeholder="Inserisci la nuova password" value={nuova} autoFocus={desktop}
                   type={mostra ? 'text' : 'password'} autoComplete="new-password"
                   errore={errore?.tipo === 'nuova'} aiuto="Minimo 8 caratteri" fine={occhio}
                   onChange={(e) => { setNuova(e.target.value); pulisci(); }} />
            <Campo id="conferma-password" etichetta="Ripeti la nuova password" icona={icona(LockOutlined)}
                   placeholder="Ripeti la password" value={conferma}
                   type={mostra ? 'text' : 'password'} autoComplete="new-password"
                   errore={errore?.tipo === 'conferma'} fine={occhio}
                   onChange={(e) => { setConferma(e.target.value); pulisci(); }} />
          </>
        )}

        <Button type="submit" variant="contained" disabled={busy || verificaLink} fullWidth
                endIcon={busy || verificaLink ? null : <ArrowForward />}
                sx={{ py: 1.6, borderRadius: 2.5, fontSize: 17, fontWeight: 700,
                      boxShadow: '0 6px 16px rgba(29,78,216,.28)' }}>
          {busy || verificaLink ? <CircularProgress size={22} color="inherit" />
            : cambio ? 'Salva e continua'
            : vista === 'reimposta' ? (resetUtente ? 'Salva la password' : 'Chiedi un nuovo link')
            : vista === 'dimenticata' ? (inviato ? "Torna all'accesso" : 'Invia il link')
            : 'Entra'}
        </Button>
        {!cambio && !(vista === 'dimenticata' && inviato) && (
          <Button variant="text" onClick={() => vaiA(vista === 'accesso' ? 'dimenticata' : 'accesso')}
                  sx={{ alignSelf: 'center', fontWeight: 600, mt: '8px !important' }}>
            {vista === 'accesso' ? 'Non ricordi la password?' : "Torna all'accesso"}
          </Button>
        )}

        <Box sx={{ pt: 1 }}><Profilo larghezza={desktop ? 240 : 200} /></Box>
      </Stack>
    </Paper>
  );

  // Desktop: immagine a tutto schermo ancorata a sinistra (il cartellone resta sempre
  // in vista), scheda a destra. Il colore sotto è quello del cielo, per il caricamento.
  if (desktop) return (
    <Box sx={{ position: 'relative', minHeight: '100dvh', display: 'flex', alignItems: 'center',
               justifyContent: 'flex-end', pr: MARGINE, pl: 4, py: 2,
               bgcolor: '#cfe0f2', backgroundImage: `url(${sfondo})`, backgroundRepeat: 'no-repeat',
               backgroundSize: `auto ${ALTEZZA_SFONDO}`, backgroundPosition: '0% 30%' }}>
      {conTitolo && <Titolo />}
      {scheda}
    </Box>
  );

  // Telefono: la stessa immagine a tutta altezza, velata perché la scheda resti protagonista.
  // Il palo del cartellone (x = 286 su un'altezza di 992) cade al centro, sotto la scheda,
  // che così fa da manifesto; su schermi più larghi l'immagine resta ancorata a sinistra.
  return (
    <Box sx={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center',
               px: { xs: 2, sm: 3 }, py: 2, bgcolor: VELO,
               backgroundImage: `linear-gradient(${VELO_TRASPARENTE}, ${VELO_TRASPARENTE}), url(${sfondo})`,
               backgroundRepeat: 'no-repeat', backgroundSize: 'cover, auto 100%',
               backgroundPosition: 'center, min(0px, calc(50vw - 28.83vh)) center' }}>
      {scheda}
    </Box>
  );
}
